import { useEffect, useState, type ReactNode } from "react";
import { Avatar, Btn, DataTable, HeroTile, Icon, MeterBar, Pill, StatusPill, T, type TableColumn } from "@v0y4ge/jrni-ui";
import { useNav } from "../../shell/router";
import { can } from "../../shell/access";
import { useT } from "../../lib/i18n";
import { RowActions } from "../../shell/composites";
import { tabExport } from "../../shell/list-composites";
import { eventActions } from "./eventActions";
import type { UIEvent } from "../../lib/events-adapter";

/** Payments (2026-08-04) — minor-units → display money for the Revenue column
 * and the Paid pill. Symbol from the row currency; defaults to £. Presentational
 * only (the UI never computes an amount — it formats what the adapter carries). */
function fmtMoney(minor?: number, currency?: string): string {
  if (minor == null) return "—";
  const sym = currency === "USD" ? "$" : currency === "EUR" ? "€" : "£";
  return `${sym}${(minor / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

/**
 * The Events status vocabulary — ONE map (2026-08-19, §3). The pill's five-branch
 * ternary here and `STATUS_ORDER` in EventsList were two copies of the same list,
 * free to drift; a status added to one and not the other sorted or painted wrong.
 * Order drives the "Status" sort; tone + label drive the StatusPill.
 */
export const EVENT_STATUS: Record<string, { order: number; tone: React.ComponentProps<typeof StatusPill>["tone"]; labelKey: string; label: string }> = {
  live:     { order: 0, tone: "success", labelKey: "events.status.live",      label: "Live" },
  upcoming: { order: 1, tone: "primary", labelKey: "events.status.upcoming",  label: "Upcoming" },
  draft:    { order: 2, tone: "neutral", labelKey: "events.status.draft",     label: "Draft" },
  canceled: { order: 3, tone: "danger",  labelKey: "events.status.cancelled", label: "Cancelled" },
  past:     { order: 4, tone: "neutral", labelKey: "events.status.ended",     label: "Ended" },
};

/**
 * EventsInlineTable — the Events page table. Migrated (2026-08-06) from a
 * hand-rolled `<table>` + hand-rolled bulk bar to the jrni-ui `DataTable`
 * (typed columns, header sort, row selection, selection toolbar). The cell
 * content — hero tile, Paid/Free pill, capacity bar, host Avatar, status Pill,
 * RowActions — is preserved verbatim; only the table chrome is now a common
 * component. Sort is driven by the parent (EventsList owns the toggle); the
 * DataTable reports which column header was clicked.
 *
 * Approvals column gated off until the approvals data layer ports (flag);
 * bulk Export builds a real CSV of the selected rows (tabExport); bulk Archive
 * is honest-disabled — no events-archive op exists at 93196e63 (REQ-084).
 */
export function EventsInlineTable({ events, sortKey, sortDir, onSort, folderChipsFor, foldersItemFor, onBulkFolderMove }: {
  events: UIEvent[];
  sortKey: string;
  sortDir: "asc" | "desc";
  onSort?: (sortId: string) => void;
  /** Folder Library hooks (2026-08-13) — chips by the title and a Folders… row
   * action, both supplied by EventsList's useFolderLibrary. Optional and
   * no-ops when absent so other callers are unaffected. */
  folderChipsFor?: (id: string) => ReactNode;
  foldersItemFor?: (id: string) => { icon: ReactNode; label: string; onClick: (e?: unknown) => void };
  /** Bulk "Move to folder…" for the TICKED rows (2026-09-18, PP).
   *
   *  The folder shell ships its own purple selection bar carrying this action,
   *  but it reads `lib.sel` — and this table owns its selection privately in
   *  `DataTable`, so that bar could never appear here and ticking rows offered
   *  no way to file them. Rather than run two selection models, the action
   *  joins the toolbar the ticks already raise, and the caller syncs the ids
   *  into the shell before opening its picker. */
  onBulkFolderMove?: (ids: string[], at: { x: number; y: number }) => void;
}) {
  const nav = useNav();
  const t = useT();
  const approvalsOn = false; // TODO: wire ApprovalsStore when the approvals layer ports
  const paymentsOn = events.some((e) => e.isPaid != null || e.revenueMinor != null);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const visibleIds = events.map((e) => e.id);
  useEffect(() => {
    setSelected((prev) => {
      const next = new Set([...prev].filter((id) => visibleIds.includes(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [visibleIds.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  // Bulk Export does real work — CSV of the selected rows via tabExport
  // (download-then-toast). No events-archive op exists → Archive honest-disabled.
  const bulkExport = () => {
    const rows = events.filter((e) => selected.has(e.id)).map((e) => ({ title: e.title, status: e.status, date: e.date, time: e.time, location: e.location, registered: e.registered, capacity: e.capacity, revenue: e.revenueMinor != null ? fmtMoney(e.revenueMinor, e.currency) : "" }));
    tabExport("events", rows);
  };

  const columns: TableColumn<UIEvent>[] = [
    {
      id: "title", header: t("events.inlineTable.headers.event", "Event"), sortable: true, minWidth: 240,
      cell: (ev) => (
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          {/* HeroTile owns this: initials sit on on-brand ink, which stays light in
              both themes. The hand-rolled tile set the initials to the base-white
              SURFACE token, which flips to #212633 under [data-theme="dark"] — so they
              went dark-on-dark the moment the app could render dark at all. */}
          <HeroTile src={ev.heroImage ?? undefined} initials={ev.title.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()} hue={ev.hue} size={36} radius={6} />
          <div style={{ minWidth: 0 }}>
            <div style={{ color: T.n1, fontWeight: 600, display: "flex", alignItems: "center" }}>{ev.title}{folderChipsFor ? folderChipsFor(ev.id) : null}</div>
            <div style={{ color: T.soft, fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
              <span>{ev.template}</span>
              {ev.isPaid === true ? <Pill tone="primary">{ev.priceFromMinor != null ? `${t("events.inlineTable.paid", "Paid")} · ${fmtMoney(ev.priceFromMinor, ev.currency)}` : t("events.inlineTable.paid", "Paid")}</Pill> :
                ev.isPaid === false ? <Pill tone="neutral">{t("events.inlineTable.free", "Free")}</Pill> : null}
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "when", header: t("events.inlineTable.headers.when", "When"), sortable: true,
      cell: (ev) => (<div><div style={{ color: T.n1 }}>{ev.date}</div><div style={{ color: T.soft, fontSize: 12 }}>{ev.time}</div></div>),
    },
    { id: "location", header: t("events.inlineTable.headers.location", "Location"), sortable: true, cell: (ev) => ev.location },
    {
      id: "host", header: t("events.inlineTable.headers.host", "Host"), sortable: true,
      cell: (ev) => (<div style={{ display: "flex", alignItems: "center", gap: 8 }}><Avatar size={24} initials={ev.host.split(" ").map((w) => w[0]).join("")} color={ev.hue} />{ev.host}</div>),
    },
    {
      id: "registered", header: t("events.inlineTable.headers.registered", "Registered"), sortable: true,
      cell: (ev) => (
        ev.capacity > 0 ? (
          /* Green when live is a STATUS colour, not a fill threshold — hence an
             explicit tone rather than `thresholds`. */
          <MeterBar value={ev.registered} max={ev.capacity} size="sm" showValue
            {...(ev.status === "live" ? { tone: "success" as const } : null)}
            aria-label={t("events.inlineTable.headers.registered", "Registered")} />
        ) : (
          /* Unlimited capacity: MeterBar cannot express ∞ (there is no max to fill
             against) and the ∞ readout is honest, so the cell renders it itself. */
          <div style={{ color: T.n1, fontWeight: 600 }}>{ev.registered}<span style={{ color: T.soft, fontWeight: 400 }}> / ∞</span></div>
        )
      ),
    },
    ...(paymentsOn ? [{
      id: "revenue", header: t("events.inlineTable.headers.revenue", "Revenue"), align: "right" as const,
      cell: (ev: UIEvent) => (ev.isPaid ? <span style={{ color: T.n1, fontWeight: 600 }}>{fmtMoney(ev.revenueMinor, ev.currency)}</span> : <span style={{ color: T.soft }}>—</span>),
    }] : []),
    {
      id: "status", header: t("events.inlineTable.headers.status", "Status"), sortable: true,
      cell: (ev) => (
        (() => {
          const st = EVENT_STATUS[ev.status] ?? EVENT_STATUS.upcoming;
          return <StatusPill tone={st.tone} dot>{t(st.labelKey, st.label)}</StatusPill>;
        })()
      ),
    },
    {
      id: "actions", header: t("events.inlineTable.headers.actions", "Actions"), align: "right" as const, minWidth: 160,
      cell: (ev) => (
        <div onClick={(e) => e.stopPropagation()}>
          <RowActions
            /* Preview (2026-08-25) — the designer's own preview of this event's
               journey pages, keyed `event:<id>`. `event.public` was a Studio
               route that 404s (the live page lives on the attend.* host, which
               is what "Open public page" below opens). */
            onView={() => nav.go("newjourneys", { journey: `event:${ev.id}`, preview: "1" })}
            viewLabel={t("events.inlineTable.actions.preview", "Preview")}
            onEdit={() => eventActions.editEvent(ev, nav)}
            editLabel={t("events.inlineTable.actions.editDetails", "Edit details")}
            onDuplicate={() => eventActions.duplicateEvent(ev)}
            audit={{ type: "event", id: ev.id, name: ev.title }}
            moreHeader={ev.title}
            moreItems={[
              { icon: <Icon.settings size={14} />, label: t("events.inlineTable.actions.manageEvent", "Manage event"), sub: t("events.inlineTable.actions.registrationsCheckInDetails", "Registrations, check-in & details"), onClick: () => nav.openEvent(ev) },
              { icon: <Icon.external size={14} />, label: t("events.inlineTable.actions.openPublicPage", "Open public page"), onClick: () => eventActions.openLandingPage(ev, nav) },
              { icon: <Icon.link size={14} />, label: t("events.inlineTable.actions.copyPublicUrl", "Copy public URL"), onClick: () => eventActions.copyPublicUrl(ev) },
              "divider",
              ...(foldersItemFor ? [foldersItemFor(ev.id)] : []),
              { icon: <Icon.layers size={14} />, label: t("events.inlineTable.actions.saveAsTemplate", "Save as template"), onClick: () => eventActions.saveAsTemplate(ev, nav) },
              { icon: <Icon.download size={14} />, label: t("events.inlineTable.actions.exportGuestsCsv", "Export guests (CSV)"), onClick: () => eventActions.exportGuestList(ev) },
              "divider",
              ev.status === "draft"
                ? { icon: <Icon.trash size={14} />, label: t("events.inlineTable.actions.deleteDraft", "Delete draft"), danger: true, onClick: () => eventActions.cancelOrDiscard(ev, nav) }
                : { icon: <Icon.x size={14} />, label: t("events.inlineTable.actions.cancelEvent", "Cancel event"), danger: true, sub: t("events.inlineTable.actions.notifiesRegisteredGuests", "Notifies registered guests"), onClick: () => eventActions.cancelOrDiscard(ev, nav) },
            ]}
          />
        </div>
      ),
    },
  ];

  return (
    <DataTable<UIEvent>
      columns={columns}
      rows={events}
      getRowId={(e) => e.id}
      selectedIds={selected}
      onRowSelect={(id, sel) => setSelected((s) => { const n = new Set(s); if (sel) n.add(id); else n.delete(id); return n; })}
      onSelectAll={(sel) => setSelected(sel ? new Set(events.map((e) => e.id)) : new Set())}
      sort={{ columnId: sortKey, direction: sortDir }}
      onSortChange={(next) => onSort?.(next.columnId)}
      onRowClick={(ev) => nav.openEvent(ev)}
      selectionNoun={t("events.inlineTable.selected", "selected")}
      bulkActions={<>
        {onBulkFolderMove && can("events.write") ? <Btn variant="ghost" size="sm" icon={<Icon.folder size={12} />} onClick={(e) => onBulkFolderMove(Array.from(selected), { x: (e as React.MouseEvent).clientX, y: (e as React.MouseEvent).clientY })}>{t("events.inlineTable.bulk.moveToFolder", "Move to folder…")}</Btn> : null}
        {can("reports.export") ? <Btn variant="ghost" size="sm" icon={<Icon.csv size={12} />} onClick={bulkExport}>{t("events.inlineTable.bulk.export", "Export")}</Btn> : null}
        {can("events.write") ? <Btn variant="ghost" size="sm" icon={<Icon.archive size={12} />} disabled title={t("events.inlineTable.bulk.archiveUnavailable", "Not available yet — no events-archive operation is declared in the wiring kit (REQ-084).")}>{t("events.inlineTable.bulk.archive", "Archive")}</Btn> : null}
      </>}
    />
  );
}
